import type { ComputedRef } from 'vue';
import { updateAppearanceSettings } from '~/api/userSettings';
import { useToastService } from '~/composables/useToastService';
import {
  type Appearance,
  type AppearancePatch,
  type AppearanceSetting,
  DEFAULT_APPEARANCE,
  DEFAULT_SETTINGS,
  appearanceAttributes,
  normalizeAppearance,
} from '~/utils/appearance';

export const APPEARANCE_STORAGE_KEY = 'gd.appearance';

const appearance = ref<Appearance>({ ...DEFAULT_APPEARANCE });
const readonlyAppearance = readonly(appearance);
const isDark = computed(() => appearance.value.colorMode === 'dark');
const isDefault = computed(() => (Object.keys(DEFAULT_SETTINGS) as AppearanceSetting[])
  .every(key => appearance.value[key] === DEFAULT_SETTINGS[key]));
let revision = 0;

function applyToDocument (value: Appearance): void {
  const root = document.documentElement;
  for (const [name, attribute] of Object.entries(appearanceAttributes(value))) {
    if (attribute === null) { root.removeAttribute(name); } else { root.setAttribute(name, attribute); }
  }
}

function readCache (): Appearance {
  try {
    const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    return normalizeAppearance(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_APPEARANCE };
  }
}

function writeCache (value: Appearance): void {
  try {
    window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage lève en navigation privée.
  }
}

function commit (value: Appearance): void {
  appearance.value = value;
  applyToDocument(value);
  writeCache(value);
}

function sameAppearance (a: Appearance, b: Appearance): boolean {
  return (Object.keys(a) as Array<keyof Appearance>).every(key => a[key] === b[key]);
}

export function initAppearance (): void {
  commit(readCache());
}

export function appearanceRevision (): number {
  return revision;
}

export function adoptServerAppearance (raw: unknown, readAtRevision?: number): void {
  if (readAtRevision !== undefined && (readAtRevision !== revision || inFlight !== null)) { return; }
  const next = normalizeAppearance(raw, appearance.value);
  if (!sameAppearance(next, appearance.value)) { commit(next); }
}

let inFlight: Promise<void> | null = null;
let unsent: AppearancePatch = {};

async function flush (): Promise<void> {
  try {
    while (Object.keys(unsent).length > 0) {
      const patch = unsent;
      unsent = {};
      try {
        await updateAppearanceSettings(patch);
      } catch (error) {
        console.error('[useAppearance] save', error);
        useToastService().add({
          severity: 'error',
          summary: 'Apparence',
          detail: error instanceof Error ? error.message : 'L\'apparence n\'a pas pu être enregistrée.',
          life: 3000,
        });
      }
    }
  } finally {
    inFlight = null;
  }
}

function send (patch: AppearancePatch): Promise<void> {
  unsent = { ...unsent, ...patch };
  inFlight ??= flush();
  return inFlight;
}

function save (choices: Partial<Appearance>, patch: AppearancePatch = choices): Promise<void> {
  const next: Appearance = { ...appearance.value, ...choices };
  if (sameAppearance(next, appearance.value)) { return inFlight ?? Promise.resolve(); }
  revision += 1;
  commit(next);
  return send(patch);
}

const FORGET_SETTINGS: AppearancePatch = Object.fromEntries(Object.keys(DEFAULT_SETTINGS).map(key => [key, null]));

export function useAppearance (): {
  appearance: Readonly<Ref<Appearance>>;
  isDark: ComputedRef<boolean>;
  isDefault: ComputedRef<boolean>;
  setSetting: <K extends AppearanceSetting>(key: K, value: Appearance[K]) => Promise<void>;
  toggleColorMode: () => Promise<void>;
  resetToDefaults: () => Promise<void>;
} {
  return {
    appearance: readonlyAppearance,
    isDark,
    isDefault,
    setSetting: (key, value) => save({ [key]: value }),
    toggleColorMode: () => save({ colorMode: isDark.value ? 'light' : 'dark' }),
    resetToDefaults: () => save(DEFAULT_SETTINGS, FORGET_SETTINGS),
  };
}
